import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';

import { NotificationDto } from '../models/payment-notification';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private hubConnection?: signalR.HubConnection;

  private connectionUrl = `${environment.baseUrl}/notification-hub`;

  private notificationSubject = new Subject<NotificationDto>();
  notification$ = this.notificationSubject.asObservable();

  private notificationsListSubject = new BehaviorSubject<NotificationDto[]>([]);
  notificationsList$ = this.notificationsListSubject.asObservable();

  constructor(
    private ngZone: NgZone,
    private http: HttpClient,
  ) { }

  startConnection(): Promise<void> {
    const token =
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      '';

    if (!token) {
      console.warn('SignalR not started: no token');
      return Promise.resolve();
    }

    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      this.loadInitialNotifications();
      return Promise.resolve();
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.connectionUrl, {
        withCredentials: true,
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.setupListeners();

    return this.hubConnection
      .start()
      .then(() => {
        console.log('SignalR connected');
        console.log('ConnectionId:', this.hubConnection?.connectionId);

        this.loadInitialNotifications();
      })
      .catch((error) => {
        console.error('SignalR connection failed:', error);
        return Promise.reject(error);
      });
  }

  private setupListeners(): void {
    if (!this.hubConnection) return;

    this.hubConnection.off('ReceiveNotification');

    this.hubConnection.on(
      'ReceiveNotification',
      (notification: NotificationDto) => {
        this.ngZone.run(() => {
          console.log('ReceiveNotification:', notification);

          const normalized = this.normalizeNotification(notification);

          this.notificationSubject.next(normalized);
          this.addNotification(normalized);
        });
      },
    );

    this.hubConnection.onreconnecting((error) => {
      console.warn('SignalR reconnecting:', error);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('SignalR reconnected:', connectionId);
      this.loadInitialNotifications();
    });

    this.hubConnection.onclose((error) => {
      console.error('SignalR closed:', error);
    });
  }

  loadInitialNotifications(): void {
    this.getAllNotificationByUser().subscribe({
      next: (res: any) => {
        console.log('GetAllNotificationByUser Full Response:', res);

        const notifications = res?.result?.returnData || res?.result || [];

        console.log('Notifications List:', notifications);

        const normalized = notifications.map((x: any) =>
          this.normalizeNotification(x),
        );

        console.log('Normalized Notifications:', normalized);

        this.setNotifications(normalized);
      },

      error: (error) => {
        console.error('Failed to load notifications:', error);
      },
    });
  }

  getAllNotificationByUser() {
    const token = localStorage.getItem('accessToken');
    const lang = localStorage.getItem('lang') || '';

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'Accept-Language': lang,
    });

    return this.http.get(
      `${environment.baseUrl}/api/services/app/Notification/GetUserNotifications`,
      { headers }
    );
  }

  private normalizeNotification(x: any): NotificationDto {
    return {
      id: x.id ?? x.Id,
      userId: x.userId ?? x.UserId,
      title: x.title ?? x.Title,
      message: x.message ?? x.Message,
      notificationType: x.notificationType ?? x.NotificationType,
      data: x.data ?? x.Data,
      creationTime: x.creationTime ?? x.CreationTime,
      isRead: x.isRead ?? x.IsRead,
      priority: x.priority ?? x.Priority,
    };
  }

  private addNotification(notification: NotificationDto): void {
    const current = this.notificationsListSubject.value;

    const exists = current.some((x) => x.id && x.id === notification.id);

    if (exists) return;

    this.notificationsListSubject.next([notification, ...current]);
  }

  setNotifications(notifications: NotificationDto[]): void {
    this.notificationsListSubject.next(notifications || []);
  }

  clearNotifications(): void {
    this.notificationsListSubject.next([]);
  }

  stopConnection(): Promise<void> {
    if (!this.hubConnection) return Promise.resolve();

    return this.hubConnection.stop();
  }

  isConnected(): boolean {
    return this.hubConnection?.state === signalR.HubConnectionState.Connected;
  }

  getConnectionState(): string {
    return this.hubConnection?.state || 'Disconnected';
  }
}
